import {
  doc,
  setDoc,
  updateDoc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  FirestoreError,
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, storage, functions } from './firebase'; // Ensure functions is exported from firebase.ts
import { User, RecurringAvailability, UserAvailability } from '@planacle/shared';
import { AvatarUploadError, validateAvatarFile } from './avatarValidation';

export const updateUserInterests = async (uid: string, interests: Record<string, string[]>) => {
  try {
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, { interests });
  } catch (error) {
    console.error('[updateUserInterests] failed:', error);
    throw error;
  }
};

export const getUserProfile = async (uid: string): Promise<User | null> => {
  const userRef = doc(db, 'users', uid);
  const userSnap = await getDoc(userRef);

  if (userSnap.exists()) {
    return userSnap.data() as User;
  } else {
    return null;
  }
};

export const updateUserAvailability = async (
  _uid: string,
  newAvailability: RecurringAvailability
) => {
  try {
    // We don't use uid here because the Cloud Function uses the auth context of the caller
    // Kept _uid arg to minimize refactor signature changes in consumers
    const updateFn = httpsCallable(functions, 'updateAvailability');
    await updateFn({ availability: newAvailability });
  } catch (error) {
    console.error('[updateUserAvailability] failed:', error);
    throw error;
  }
};

/**
 * H-396: Fetch the caller's saved availability from the global `users/{uid}.availability`
 * doc via the `getAvailability` CF (which decrypts the M-285 ciphertext server-side).
 * The in-room Schedule editor needs this because `submitEventPreferences` strips availability
 * from the participant doc, so it's the only source of truth for re-hydrating the editor.
 * Returns `{}` on any failure so callers render an empty editor rather than crash.
 */
export const getUserAvailability = async (): Promise<UserAvailability> => {
  try {
    const getFn = httpsCallable<Record<string, never>, UserAvailability>(
      functions,
      'getAvailability'
    );
    const result = await getFn({});
    return (result.data as UserAvailability) ?? {};
  } catch (error) {
    console.error('[getUserAvailability] failed:', error);
    return {};
  }
};

export const updateUserLocation = async (uid: string, location: NonNullable<User['location']>) => {
  try {
    await updateUserProfile(uid, { location });
  } catch (error) {
    console.error('[updateUserLocation] failed:', error);
    throw error;
  }
};

/**
 * H-304: create the user's Firestore profile doc client-side after signup.
 *
 * The production signup path (`createAccount` Cloud Function) writes the user doc
 * synchronously via Admin SDK. The emulator/E2E path (`signUpClientSide` in
 * `AuthContext.tsx`) skips the CF for dev ergonomics — it only creates the Firebase
 * Auth user and relies on the asynchronous `onUserCreate` trigger to create the
 * Firestore doc. That trigger fires AFTER the Auth user is created, which races with
 * any immediate post-signup write (notably `OnboardingPage` calling `updateUserProfile`
 * to save interests/availability). The race surfaces as `NOT_FOUND: no entity to
 * update` in Firestore rules.
 *
 * This helper writes the doc client-side with `setDoc(..., { merge: true })`, mirroring
 * the CF's payload shape (`uid` + `email` + `displayName` + `photoURL` + timestamps +
 * empty `interests`/`availability` defaults). The `onUserCreate` trigger is already
 * idempotent (`if (!snapshot.exists)` guard at line 24 of `userTriggers.ts`), so
 * client-side + trigger writes coexist safely — the trigger no-ops when it sees the
 * doc already exists.
 *
 * Use `setDoc + merge` (NOT `updateDoc`) so:
 *   - first-time signup: doc doesn't exist → setDoc creates it.
 *   - re-signup race (already-exists from createAccount or earlier signUpClientSide):
 *     setDoc merges, no data lost.
 */
export const createUserProfile = async (
  uid: string,
  data: {
    email: string | null;
    displayName: string | null;
    photoURL?: string | null;
  }
): Promise<void> => {
  try {
    const userRef = doc(db, 'users', uid);
    await setDoc(
      userRef,
      {
        uid,
        email: data.email,
        displayName: data.displayName,
        photoURL: data.photoURL ?? null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        interests: {},
        availability: {},
      },
      { merge: true }
    );
  } catch (error) {
    console.error('[createUserProfile] failed:', error);
    throw error;
  }
};

// H-440: Persist tosConsentAt via server callable so the timestamp is server-generated.
// Used by Google and client-side-fallback signup paths where createAccount is not invoked.
export const recordConsent = async (): Promise<void> => {
  const fn = httpsCallable(functions, 'recordConsent');
  await fn({});
};

export const updateUserProfile = async (uid: string, data: Partial<User>) => {
  try {
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, data);
  } catch (error) {
    console.error('[updateUserProfile] failed:', error);
    throw error;
  }
};

export const updateNotificationsSeenAt = async (
  userId: string,
  updates: Record<string, string>
): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, updates);
  } catch (error) {
    console.error('[updateNotificationsSeenAt] failed:', error);
    throw error;
  }
};

export const subscribeToUserNotificationDoc = (
  userId: string,
  callback: (seenAt: Record<string, string>) => void,
  onError?: (error: FirestoreError) => void
): (() => void) => {
  return onSnapshot(
    doc(db, 'users', userId),
    (docSnap) => {
      if (docSnap.exists()) {
        callback((docSnap.data().notificationsSeenAt as Record<string, string>) ?? {});
      }
    },
    onError
  );
};

export const uploadAvatar = async (
  uid: string,
  file: File,
  onProgress?: (percentage: number) => void
): Promise<string> => {
  validateAvatarFile(file);
  try {
    const storageRef = ref(storage, `users/${uid}/avatar`);
    const task = uploadBytesResumable(storageRef, file, { contentType: file.type });
    await new Promise<void>((resolve, reject) => {
      task.on(
        'state_changed',
        (snapshot) => {
          const total = snapshot.totalBytes || file.size || 1;
          onProgress?.(Math.round((snapshot.bytesTransferred / total) * 100));
        },
        reject,
        resolve
      );
    });
    const downloadURL = await getDownloadURL(storageRef);
    await updateUserProfile(uid, { photoURL: downloadURL });
    return downloadURL;
  } catch (error) {
    console.error('[uploadAvatar] failed:', error);
    if (error instanceof AvatarUploadError) throw error;
    throw new AvatarUploadError(
      'upload-failed',
      "We couldn't upload that photo. Try again.",
      error
    );
  }
};
